<template>
    <div class="p-4 sm:ml-64 mt-16">
        <h1 class="text-2xl font-semibold mb-4">Detail Transaksi</h1>
        <p v-if="loading">Loading...</p>
        <div v-else>
            <h2 class="text-xl font-bold">{{ user.name }}</h2>
            <p class="text-gray-500">{{ user.email }}</p>

            <!-- Date Range Picker -->
            <div class="flex items-center space-x-4 mt-4">
                <input type="date" v-model="startDate"
                    class="border border-gray-300 text-gray-900 text-sm rounded-lg p-2" />
                <span class="text-gray-500">sampai</span>
                <input type="date" v-model="endDate"
                    class="border border-gray-300 text-gray-900 text-sm rounded-lg p-2" />
                <button @click="fetchTransactions"
                    class="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
                    Filter
                </button>

                <button @click="exportToExcel"
                    class="bg-emerald-500 text-white px-4 py-2 rounded-lg hover:bg-emerald-700">
                    Export Excel
                </button>
            </div>

            <!-- Table -->
            <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400 mt-4">
                <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700">
                    <tr>
                        <th class="px-6 py-3">Tgl Transaksi</th>
                        <th class="px-6 py-3">ID PEL</th>
                        <th class="px-6 py-3">Nama</th>
                        <th class="px-6 py-3">No Ref</th>
                        <th class="px-6 py-3">Admin Bank</th>
                        <th class="px-6 py-3">BL/TH</th>
                        <th class="px-6 py-3">Total Bayar</th>
                        <th class="px-6 py-3">Invoice</th>
                        <th class="px-6 py-3">Jenis</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="transaction in transactions" :key="transaction.id"
                        class="bg-white border-b dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-600">
                        <td class="px-6 py-4">{{ formatDate(transaction.timestamp) }}</td>
                        <td class="px-6 py-4">{{ transaction['IDPEL'] || '-' }}</td>
                        <td class="px-6 py-4">{{ transaction['NAMA'] || '-' }}</td>
                        <td class="px-6 py-4">{{ transaction['NO REF'] || '-' }}</td>
                        <td class="px-6 py-4">{{ transaction['Admin Bank'] || '-' }}</td>
                        <td class="px-6 py-4">{{ transaction['BL/TH'] || '-' }}</td>
                        <td class="px-6 py-4">
                            {{ transaction.type === "taglis-prabayar" ? transaction["RP BAYAR"] : transaction["TOTAL BAYAR"] || '-' }}
                        </td>
                        <td class="px-6 py-4">{{ transaction['invoice_number'] || '-' }}</td>
                        <td class="px-6 py-4">{{ transaction.type || '-' }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { getFirestore, collection, getDocs, query, where, Timestamp } from "firebase/firestore";
import * as XLSX from "xlsx";

const db = getFirestore();
const route = useRoute();
const userId = route.params.id;
const transactions = ref([]);
const user = ref({});
const loading = ref(true);
const startDate = ref("");
const endDate = ref("");

const fetchTransactions = async () => {
    try {
        loading.value = true;

        let transactionsQuery = query(collection(db, "bills"), where("uid", "==", userId));

        if (startDate.value && endDate.value) {
            const startTimestamp = Timestamp.fromDate(new Date(startDate.value));
            const endTimestamp = Timestamp.fromDate(new Date(endDate.value + "T23:59:59"));
            transactionsQuery = query(transactionsQuery, where("timestamp", ">=", startTimestamp), where("timestamp", "<=", endTimestamp));
        }

        const transactionsSnapshot = await getDocs(transactionsQuery);
        transactions.value = transactionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const userQuery = query(collection(db, "users"), where("uid", "==", userId));
        const userSnapshot = await getDocs(userQuery);
        if (!userSnapshot.empty) {
            user.value = userSnapshot.docs[0].data();
        }

        loading.value = false;
    } catch (error) {
        console.error("Error fetching transactions:", error);
    }
};

onMounted(fetchTransactions);

const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = timestamp.toDate();
    return date.toLocaleDateString("id-ID", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

// Fungsi Export ke Excel
const exportToExcel = () => {
    if (transactions.value.length === 0) {
        alert("Tidak ada data transaksi untuk diekspor.");
        return;
    }

    if (!user.value.name) {
        alert("Nama user tidak tersedia.");
        return;
    }

    // Format tanggal filter
    const formattedStartDate = startDate.value ? new Date(startDate.value).toLocaleDateString("id-ID") : "Semua";
    const formattedEndDate = endDate.value ? new Date(endDate.value).toLocaleDateString("id-ID") : "Semua";

    // Membuat header khusus
    const headerInfo = [
        [`NAMA: ${user.value.name}`], 
        [`Transaksi: ${formattedStartDate} - ${formattedEndDate}`], 
        [] // Baris kosong sebelum data transaksi
    ];

    // Mengonversi transaksi ke format Excel
    const data = transactions.value.map(transaction => ({
        "Tgl Transaksi": formatDate(transaction.timestamp),
        "ID PEL": transaction["IDPEL"] || "-",
        "Nama": transaction["NAMA"] || "-",
        "No Ref": transaction["NO REF"] || "-",
        "Admin Bank": transaction["Admin Bank"] || "-",
        "BL/TH": transaction["BL/TH"] || "-",
        "Total Bayar": transaction.type === "taglis-prabayar" ? transaction["RP BAYAR"] : transaction["TOTAL BAYAR"] || "-",
        "Invoice": transaction["invoice_number"] || "-",
        "Jenis": transaction.type || "-"
    }));

    // Menggabungkan header dengan data transaksi
    const worksheetData = [...headerInfo, ...XLSX.utils.sheet_to_json(XLSX.utils.json_to_sheet(data), { header: 1 })];

    // Membuat worksheet dan workbook
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transaksi");

    // Menggunakan nama user dalam nama file
    const fileName = `${user.value.name.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.xlsx`;

    // Menyimpan file
    XLSX.writeFile(wb, fileName);
};
</script>
